import { Toaster } from "@/components/ui/toaster";
import { Toaster as SonnerToaster } from "sonner";
import { QueryClientProvider } from '@tanstack/react-query';
import { queryClientInstance } from '@/lib/query-client';
import { BrowserRouter as Router, Route, Routes, Navigate, useParams } from 'react-router-dom';
import PageNotFound from './lib/PageNotFound';
import { AuthProvider, useAuth } from '@/lib/AuthContext';
import { I18nProvider } from '@/lib/i18n';
import { PlatformCurrencyProvider } from '@/lib/platform-currency';
import UserNotRegisteredError from '@/components/UserNotRegisteredError';
import AppErrorBoundary from '@/components/shared/AppErrorBoundary';
import LegacyRedirect from '@/components/shared/LegacyRedirect';
import studentLegacyRedirectRoutes from '@/routes/StudentLegacyRedirects';
import studentInternalRoutes from '@/routes/StudentInternalRoutes';
import academyPublicRoutes, { academyNotFoundRoute, AcademyHome as AcademyHomeFallback } from '@/routes/AcademyPublicRoutes';
import { Suspense } from 'react';
import { lazyWithRetry as lazy } from '@/lib/lazy-with-retry';
import AdminImpersonationBanner from '@/components/admin/AdminImpersonationBanner';
import FallbackReasonToast from '@/components/shared/FallbackReasonToast';
import { BrandSpinner } from '@/components/shared/ContentSpinner';
import { isCustomAcademyHost } from '@/lib/host-resolver';
const CustomDomainShell = lazy(() => import('./components/student/CustomDomainShell'));
const CustomDomainProtectedRedirect = lazy(() => import('./components/student/CustomDomainProtectedRedirect'));
const CustomDomainStudentAreaGate = lazy(() => import('./components/student/CustomDomainStudentAreaGate'));
const DomainCheck = lazy(() => import('./pages/public/DomainCheck'));
const AuthHandoff = lazy(() => import('./pages/student/AuthHandoff'));

// Inline redirect helper: legacy /instructor/payments/bank-transfers/:id → /instructor/wallet/transfers?focus=:id
// Edge functions used to emit those URLs in notifications/emails before the wallet shell unification.
function BankTransferFocusRedirect({ base }: { base: string }) {
  const { id } = useParams();
  if (!id) return <Navigate to={base} replace />;
  return <Navigate to={`${base}?focus=${id}`} replace />;
}

// Only PublicLayout loaded eagerly (needed for landing page shell)
import PublicLayout from './components/public/PublicLayout';
import AuthTransitionLayout from './components/shared/AuthTransitionLayout';

// Other layouts lazy-loaded
const InstructorLayout = lazy(() => import('./components/instructor/InstructorLayout'));
const AdminLayout = lazy(() => import('./components/admin/AdminLayout'));
const AcademySlugShell = lazy(() => import('./components/student/AcademySlugShell'));

// Landing page loaded eagerly (LCP element)
import Landing from './pages/public/Landing';

const Signup = lazy(() => import('./pages/public/Signup'));
const Login = lazy(() => import('./pages/public/Login'));
const ResetPassword = lazy(() => import('./pages/public/ResetPassword'));
const ForgotPassword = lazy(() => import('./pages/public/ForgotPassword'));
const Terms = lazy(() => import('./pages/public/Terms'));
const Privacy = lazy(() => import('./pages/public/Privacy'));
const PostLogin = lazy(() => import('./pages/public/PostLogin'));
const CreateAcademy = lazy(() => import('./pages/public/CreateAcademy'));
const AcademySelect = lazy(() => import('./pages/public/AcademySelect'));
const AccountNeutral = lazy(() => import('./pages/public/AccountNeutral'));
const Onboarding = lazy(() => import('./pages/instructor/Onboarding'));
const InstructorDashboard = lazy(() => import('./pages/instructor/Dashboard'));
// Settings.jsx is now a legacy redirect — handled inline by SettingsShell + nested routes.
const InstructorTeam = lazy(() => import('./pages/instructor/TeamManagement'));
const InstructorTemplates = lazy(() => import('./pages/instructor/TemplateSelection'));
const InstructorPages = lazy(() => import('./pages/instructor/Pages'));
const InstructorPageEditor = lazy(() => import('./pages/instructor/PageEditor'));
const InstructorProducts = lazy(() => import('./pages/instructor/Products'));
const InstructorProductForm = lazy(() => import('./pages/instructor/ProductForm'));
const InstructorEntityReviews = lazy(() => import('./pages/instructor/EntityReviews'));
const InstructorCourses = lazy(() => import('./pages/instructor/Courses'));
const InstructorCourseForm = lazy(() => import('./pages/instructor/CourseForm'));
const InstructorCourseContent = lazy(() => import('./pages/instructor/CourseContent'));
const InstructorCoursePageEditor = lazy(() => import('./pages/instructor/CoursePageEditor'));
const InstructorLessonEditor = lazy(() => import('./pages/instructor/LessonEditor'));
const InstructorQuizEditor = lazy(() => import('./pages/instructor/QuizEditor'));
const InstructorQuestionBank = lazy(() => import('./pages/instructor/QuestionBank'));
const InstructorMessages = lazy(() => import('./pages/instructor/Messages'));
const InstructorStudents = lazy(() => import('./pages/instructor/Students'));
const InstructorStudentDetail = lazy(() => import('./pages/instructor/StudentDetail'));
const InstructorEmailTemplates = lazy(() => import('./pages/instructor/EmailTemplates'));
const InstructorEmailTemplateEditor = lazy(() => import('./pages/instructor/EmailTemplateEditor'));
const InstructorEmailLogs = lazy(() => import('./pages/instructor/EmailLogs'));
const InstructorEmailCampaign = lazy(() => import('./pages/instructor/EmailCampaign'));
const InstructorSessions = lazy(() => import('./pages/instructor/Sessions'));
const InstructorSessionDetail = lazy(() => import('./pages/instructor/SessionDetail'));
const InstructorWalletOverview = lazy(() => import('./pages/instructor/WalletOverview'));
const InstructorBankTransferReview = lazy(() => import('./pages/instructor/BankTransferReview'));
const InstructorMembershipBankReview = lazy(() => import('./pages/instructor/payments/MembershipBankReview'));
const InstructorCoupons = lazy(() => import('./pages/instructor/Coupons'));
const InstructorSentInvites = lazy(() => import('./pages/instructor/SentInvites'));
const InstructorMarketingGrants = lazy(() => import('./pages/instructor/MarketingGrants'));
const InstructorCertificateTemplates = lazy(() => import('./pages/instructor/CertificateTemplates'));
import ModuleLockGuard from "./components/common/ModuleLockGuard";
import { FEATURE_KEYS } from "./lib/feature-keys";
const InstructorComments = lazy(() => import('./pages/instructor/Comments'));
const InstructorDataExport = lazy(() => import('./pages/instructor/DataExport'));
const InstructorNotificationHistory = lazy(() => import('./pages/instructor/NotificationHistory'));
const InstructorFeatureRequests = lazy(() => import('./pages/instructor/FeatureRequests'));
const InstructorSupport = lazy(() => import('./pages/instructor/InstructorSupport'));
const ProductPageEditor = lazy(() => import('./pages/instructor/ProductPageEditor'));
const InstructorSessionPageEditor = lazy(() => import('./pages/instructor/SessionPageEditor'));
const InstructorSessionTypePageEditor = lazy(() => import('./pages/instructor/SessionTypePageEditor'));

// New shells
const TeachingShell = lazy(() => import('./components/instructor/shells/TeachingShell'));
const PeopleShell = lazy(() => import('./components/instructor/shells/PeopleShell'));
const CommunicationsShell = lazy(() => import('./components/instructor/shells/CommunicationsShell'));
const EmailsSubShell = lazy(() => import('./components/instructor/shells/EmailsSubShell'));
const InstructorReviewsLayout = lazy(() => import('./pages/instructor/communications/reviews/ReviewsLayout'));
const InstructorReviewsList = lazy(() => import('./pages/instructor/communications/reviews/ReviewsList'));
const InstructorReviewsAnalytics = lazy(() => import('./pages/instructor/communications/reviews/ReviewsAnalytics'));
const InstructorReviewsSettings = lazy(() => import('./pages/instructor/communications/reviews/ReviewsSettings'));
const AdminAcademyReviews = lazy(() => import('./pages/admin/AcademyReviews'));
const AdminReviewsLayout = lazy(() => import('./pages/admin/reviews/ReviewsLayout'));
const AdminReviewsOverview = lazy(() => import('./pages/admin/reviews/OverviewTab'));
const AdminReviewsAcademies = lazy(() => import('./pages/admin/reviews/AcademiesTab'));
const AdminReviewsContent = lazy(() => import('./pages/admin/reviews/ContentTab'));
const AdminReviewsPlatform = lazy(() => import('./pages/admin/reviews/PlatformTab'));
const ReviewsHubLayout = lazy(() => import('./pages/instructor/reviews/ReviewsHubLayout'));
const ReviewsHubOverview = lazy(() => import('./pages/instructor/reviews/Overview'));
const ReviewsHubAcademy = lazy(() => import('./pages/instructor/reviews/AcademyTab'));
const ReviewsHubContent = lazy(() => import('./pages/instructor/reviews/ContentTab'));
const ReviewsHubPlatform = lazy(() => import('./pages/instructor/reviews/PlatformTab'));
const MarketingShell = lazy(() => import('./components/instructor/shells/MarketingShell'));
const MarketingDefaultRedirect = lazy(() => import('./components/instructor/shells/MarketingDefaultRedirect'));
const WalletShell = lazy(() => import('./components/instructor/shells/WalletShell'));
const SettingsShell = lazy(() => import('./components/instructor/shells/SettingsShell'));

// Settings nested pages
const SettingsIdentityPage = lazy(() => import('./pages/instructor/settings/SettingsIdentityPage'));
const SettingsSitePresencePage = lazy(() => import('./pages/instructor/settings/SettingsSitePresencePage'));
const SettingsFinancePage = lazy(() => import('./pages/instructor/settings/SettingsFinancePage'));
const SettingsBillingPlanPage = lazy(() => import('./pages/instructor/settings/SettingsBillingPlanPage'));
const ProductsMembershipPage = lazy(() => import('./pages/instructor/products/MembershipPage'));
const ProductsMembershipLandingEditor = lazy(() => import('./pages/instructor/products/MembershipLandingEditor'));
const ProductsShell = lazy(() => import('./components/instructor/shells/ProductsShell'));
const SettingsIntegrationsPage = lazy(() => import('./pages/instructor/settings/SettingsIntegrationsPage'));
const SettingsNotificationsPage = lazy(() => import('./pages/instructor/settings/SettingsNotificationsPage'));
const SettingsTrackingPage = lazy(() => import('./pages/instructor/settings/SettingsTrackingPage'));

// Student shells
const LearningShell = lazy(() => import('./components/student/shells/LearningShell'));
const ShopShell = lazy(() => import('./components/student/shells/ShopShell'));
const ConnectShell = lazy(() => import('./components/student/shells/ConnectShell'));

const Unsubscribe = lazy(() => import('./pages/student/Unsubscribe'));
const AdminLogin = lazy(() => import('./pages/admin/AdminLogin'));
const AdminDashboard = lazy(() => import('./pages/admin/Dashboard'));
const AdminAcademies = lazy(() => import('./pages/admin/Academies'));
const AcademyDetail = lazy(() => import('./pages/admin/AcademyDetail'));
const AdminDomains = lazy(() => import('./pages/admin/Domains'));
const AdminSubscriptions = lazy(() => import('./pages/admin/Subscriptions'));
const AdminPayments = lazy(() => import('./pages/admin/Payments'));
const AdminStudents = lazy(() => import('./pages/admin/Students'));
const AdminInstructors = lazy(() => import('./pages/admin/Instructors'));
const AdminAuditLog = lazy(() => import('./pages/admin/AuditLog'));
const AdminTeam = lazy(() => import('./pages/admin/AdminTeam'));
const AdminDataExport = lazy(() => import('./pages/admin/DataExport'));
const PlatformSettings = lazy(() => import('./pages/admin/PlatformSettings'));
const PlanMatrix = lazy(() => import('./pages/admin/PlanMatrix'));
const AdminProductionReleases = lazy(() => import('./pages/admin/ProductionReleases'));
const AdminMarketingShell = lazy(() => import('./pages/admin/marketing/MarketingShell'));
const PlatformCoupons = lazy(() => import('./pages/admin/marketing/PlatformCoupons'));
const CouponRedemptions = lazy(() => import('./pages/admin/marketing/CouponRedemptions'));
const AdminLandingSeo = lazy(() => import('./pages/admin/marketing/LandingSeo'));
const AdminArticles = lazy(() => import('./pages/admin/marketing/Articles'));
const AdminArticleEditor = lazy(() => import('./pages/admin/marketing/ArticleEditor'));
const AdminArticleTags = lazy(() => import('./pages/admin/marketing/Tags'));
const AdminLearningCategories = lazy(() => import('./pages/admin/learning/LearningCategories'));
const AdminLearningArticles = lazy(() => import('./pages/admin/learning/LearningArticles'));
const AdminLearningArticleEditor = lazy(() => import('./pages/admin/learning/LearningArticleEditor'));
const AdminLearningTags = lazy(() => import('./pages/admin/learning/LearningTags'));
const AdminLearningSettings = lazy(() => import('./pages/admin/learning/LearningSettings'));
const AdminAllamDashboard = lazy(() => import('./pages/admin/allam/AllamDashboard'));
const AdminSupportAnalyticsHub = lazy(() => import('./pages/admin/dashboard/SupportAnalyticsHub'));
const AdminAnnouncementsPlatformPublic = lazy(() => import('./pages/admin/marketing/AnnouncementsPlatformPublic'));
const AdminAnnouncementsInstructor = lazy(() => import('./pages/admin/marketing/AnnouncementsInstructor'));
const InstructorAnnouncements = lazy(() => import('./pages/instructor/marketing/Announcements'));
const InstructorLearning = lazy(() => import('./pages/instructor/learning/Learning'));
const InstructorLearningCategory = lazy(() => import('./pages/instructor/learning/LearningCategory'));
const InstructorLearningArticle = lazy(() => import('./pages/instructor/learning/LearningArticle'));
const PublicBlog = lazy(() => import('./pages/public/Blog'));
const PublicArticle = lazy(() => import('./pages/public/Article'));
const AdminNotifications = lazy(() => import('./pages/admin/AdminNotifications'));
const AdminBankTransferEscalations = lazy(() => import('./pages/admin/BankTransferEscalations'));
const AdminInboxShell = lazy(() => import('./components/admin/shells/AdminInboxShell'));
const AdminSupportReports = lazy(() => import('./pages/admin/AdminSupportReports'));
const AdminOperatorHealth = lazy(() => import('./pages/admin/OperatorHealth'));
const StudentDetail = lazy(() => import('./pages/admin/StudentDetail'));
const AdminAuthEmailCenter = lazy(() => import('./pages/admin/AuthEmailCenter'));
const CanvasFrame = lazy(() => import('./pages/editor/CanvasFrame'));
const AcademyHome = lazy(() => import('./pages/student/AcademyHome'));
const CourseCatalog = lazy(() => import('./pages/student/CourseCatalog'));
const CourseDetail = lazy(() => import('./pages/student/CourseDetail'));
const StudentDashboard = lazy(() => import('./pages/student/StudentDashboard'));
const StudentCourses = lazy(() => import('./pages/student/StudentCourses'));
const StudentSessions = lazy(() => import('./pages/student/StudentSessions'));
const StudentSubscriptions = lazy(() => import('./pages/student/StudentSubscriptions'));

const StudentExplore = lazy(() => import('./pages/student/StudentExplore'));
const StudentInvitesGrants = lazy(() => import('./pages/student/StudentInvitesGrants'));
const StudentPurchases = lazy(() => import('./pages/student/StudentPurchases'));
const MyReviews = lazy(() => import('./pages/student/MyReviews'));
const OwnedProductDetail = lazy(() => import('./pages/student/OwnedProductDetail'));
const StudentShopMembership = lazy(() => import('./pages/student/StudentShopMembership.jsx'));
const StudentFreeAccess = lazy(() => import('./pages/student/StudentFreeAccess'));
const StudentInbox = lazy(() => import('./pages/student/StudentInbox'));
const StudentCertificates = lazy(() => import('./pages/student/StudentCertificates'));
const StudentProfile = lazy(() => import('./pages/student/StudentProfile'));
const StudentNotificationHistory = lazy(() => import('./pages/student/NotificationHistory'));
const StudentSupport = lazy(() => import('./pages/student/StudentSupport'));
const StudentSettings = lazy(() => import('./pages/student/StudentSettings'));
const StudentGuide = lazy(() => import('./pages/student/StudentGuide'));
const StudentNotificationSettings = lazy(() => import('./pages/student/StudentNotificationSettings'));
const SessionRoom = lazy(() => import('./pages/student/SessionRoom'));
const SessionRecording = lazy(() => import('./pages/student/SessionRecording'));
const AcademyCustomPage = lazy(() => import('./pages/student/AcademyCustomPage'));
const ProductDetail = lazy(() => import('./pages/student/ProductDetail'));
const ProductsCatalog = lazy(() => import('./pages/student/ProductsCatalog'));
const SessionsCatalog = lazy(() => import('./pages/student/SessionsCatalog'));
const SessionDetail = lazy(() => import('./pages/student/SessionDetail'));
const MembershipLanding = lazy(() => import('./pages/student/MembershipLanding'));
const CourseViewer = lazy(() => import('./pages/student/CourseViewer'));
const CourseCompletionPage = lazy(() => import('./pages/student/CourseCompletionPage'));
const QuizPage = lazy(() => import('./pages/student/QuizPage'));
const EmailUnsubscribe = lazy(() => import('./pages/student/EmailUnsubscribe'));
const MeetRedirect = lazy(() => import('./pages/MeetRedirect'));
const AcademyLogin = lazy(() => import('./pages/student/AcademyLogin'));
const AcademySignup = lazy(() => import('./pages/student/AcademySignup'));
const AcademyGoogleHandoff = lazy(() => import('./pages/student/AcademyGoogleHandoff'));
const CompleteProfile = lazy(() => import('./pages/public/CompleteProfile'));
const CertificateView = lazy(() => import('./pages/CertificateView'));
const PublicCertificateView = lazy(() => import('./pages/PublicCertificateView'));
const ReceiptView = lazy(() => import('./pages/ReceiptView'));

const PageSpinner = () => (
  <div className="fixed inset-0 flex items-center justify-center">
    <BrandSpinner size="lg" />
  </div>
);

function AcademyRedirect() {
  const { slug, "*": rest } = useParams();
  const target = `/${slug}${rest ? `/${rest}` : ""}${window.location.search}`;
  return <Navigate to={target} replace />;
}

// Phase 9 — On a custom academy domain, drop any leading slug segment from
// auth URLs (e.g. /<slug>/login → /login) so legacy links built with the
// slug still work after we moved auth to the host root.
function StripSlugRedirect({ to }: { to: "login" | "signup" }) {
  const search = typeof window !== "undefined" ? window.location.search : "";
  return <Navigate to={`/${to}${search}`} replace />;
}

const AuthenticatedApp = () => {
  const { isLoadingPublicSettings, authError } = useAuth();

  if (isLoadingPublicSettings) {
    return <PageSpinner />;
  }

  if (authError && authError.type === 'user_not_registered') {
    return <UserNotRegisteredError />;
  }

  // Phase 2 — Public Browsing on Custom Academy Domains.
  // When the request is on an instructor-owned custom domain, mount a
  // host-aware Routes block. Public pages render directly under the
  // CustomDomainShell; protected/auth/instructor/admin paths redirect to
  // dwrrah.com. Platform host routing is untouched.
  const onCustomDomain = isCustomAcademyHost();
  if (onCustomDomain) {
    return (
      <AppErrorBoundary>
        <Suspense fallback={null}>
          <Routes>
            <Route path="/__domain-check" element={<DomainCheck />} />

            {/* Phase 7A — Encrypted Session Handoff receiver (custom domain only) */}
            <Route path="/auth/handoff" element={<AuthHandoff />} />

            {/* Phase 9 — Local auth on custom domain.
                /login & /signup render the academy-branded auth pages directly
                here so the student never leaves the custom domain. They are
                wrapped by CustomDomainShell which provides the academy-aware
                outlet context (via AcademySlugShell → useStudentAuth). */}
            <Route element={<CustomDomainShell />}>
              <Route path="/login" element={<AcademyLogin />} />
              <Route path="/signup" element={<AcademySignup />} />
              <Route path="/google-handoff" element={<AcademyGoogleHandoff />} />
            </Route>

            {/* Reset password / post-login / complete-profile stay on the
                main domain (email links + OAuth handoff land there). */}
            <Route path="/reset-password" element={<CustomDomainProtectedRedirect targetType="auth" path="reset-password" />} />
            <Route path="/post-login" element={<CustomDomainProtectedRedirect targetType="auth" path="post-login" />} />
            <Route path="/complete-profile" element={<CustomDomainProtectedRedirect targetType="auth" path="complete-profile" />} />

            {/* Instructor / Admin → main domain (no academy slug) */}
            <Route path="/instructor/*" element={<CustomDomainProtectedRedirect targetType="instructor" />} />
            <Route path="/admin/*" element={<CustomDomainProtectedRedirect targetType="admin" />} />

            {/* Phase 17 — Checkout always lives on dwrrah.com (Stripe success/
                cancel URLs are configured against the platform host). Intercept
                any /checkout/* link reached on a custom domain and bounce it
                back to the platform so the flow can complete. */}
            <Route path="/checkout/*" element={<CustomDomainProtectedRedirect targetType="auth" />} />

            {/* Phase 7B — Student-area paths (custom domain).
                The gate is now a layout route. When the flag is OFF, the gate
                redirects to dwrrah.com (no children render). When ON, the gate
                mounts <CustomDomainShell /> which contains an Outlet, so the
                child routes below render the actual student pages. Mirrors the
                slug-based tree (lines ~705-834). */}
            <Route element={<CustomDomainStudentAreaGate />}>
              {/* Unified student internal routes (single source of truth).
                  Identical tree on platform host (`/:slug/*`). */}
              {studentInternalRoutes()}
            </Route>

            {/* Legacy student-area redirects (parity with platform host).
                These MUST be registered BEFORE the public shell catch-all
                below so that links like `/explore/:id`, `/my-courses/:id`,
                `/my-purchases/:id` resolve to the canonical internal route
                instead of falling through to the academy landing page. */}
            {studentLegacyRedirectRoutes()}

            {/* Public browsing — mounted at root, host resolves the academy */}
            <Route element={<CustomDomainShell />}>
              <Route index element={<AcademyHomeFallback />} />
              {academyPublicRoutes()}
              {academyNotFoundRoute()}
            </Route>

            {/* Certificate viewers — full-page, RLS-protected (private) and
                share-token (public). Mounted on custom domain so links opened
                from /certificates or instructor "issued" tab don't fall through
                the catch-all to AcademyHome. Outside CustomDomainStudentAreaGate
                because instructors and anonymous share-link visitors also use
                them, and they render their own chrome. */}
            <Route path="/certificate/view/:certificateId" element={<CertificateView />} />
            <Route path="/c/:shareToken" element={<PublicCertificateView />} />

            {/* Phase 9 — legacy slug-prefixed auth links → strip slug.
                Placed OUTSIDE the public shell so we redirect immediately
                without paying for academy resolution. */}
            <Route path=":legacySlug/login" element={<StripSlugRedirect to="login" />} />
            <Route path=":legacySlug/signup" element={<StripSlugRedirect to="signup" />} />
          </Routes>
        </Suspense>
      </AppErrorBoundary>
    );
  }

  return (
    <AppErrorBoundary>
      <Suspense fallback={null}>
        <AdminImpersonationBanner />
        <FallbackReasonToast />
        <Routes>
          <Route element={<PublicLayout />}>
            <Route path="/" element={<Landing />} />
            <Route path="/signup" element={<Signup />} />
            <Route path="/login" element={<Login />} />
            <Route path="/terms" element={<Terms />} />
            <Route path="/privacy" element={<Privacy />} />
            <Route path="/blog" element={<PublicBlog />} />
            <Route path="/blog/:slug" element={<PublicArticle />} />
          </Route>

          {/* Auth-transition pages — neutral chrome (no marketing header/footer).
              These render during OAuth handoff / password reset / profile
              completion. Showing the platform marketing chrome here confuses
              students arriving from a custom academy domain. */}
          <Route element={<AuthTransitionLayout />}>
            <Route path="/post-login" element={<PostLogin />} />
            <Route path="/complete-profile" element={<CompleteProfile />} />
            <Route path="/reset-password" element={<ResetPassword />} />
            <Route path="/account-neutral" element={<AccountNeutral />} />
          </Route>

          <Route element={<PublicLayout />}>
            <Route path="/forgot-password" element={<ForgotPassword />} />
            <Route path="/create-academy" element={<CreateAcademy />} />
            <Route path="/select-academy" element={<AcademySelect />} />
          </Route>

          <Route path="/instructor/setup" element={<Onboarding />} />
          <Route element={<InstructorLayout />}>
            <Route path="/instructor/dashboard" element={<InstructorDashboard />} />
            <Route path="/instructor/onboarding" element={<Onboarding />} />
            <Route path="/instructor/templates" element={<InstructorTemplates />} />

            {/* === Landing pages (renamed from /pages) === */}
            <Route path="/instructor/landing" element={<InstructorPages />} />
            <Route path="/instructor/landing/:pageId" element={<InstructorPageEditor />} />
            {/* Legacy redirects */}
            <Route path="/instructor/pages" element={<LegacyRedirect to="/instructor/landing" />} />
            <Route path="/instructor/pages/:pageId" element={<LegacyRedirect to="/instructor/landing/:pageId" />} />

            {/* === Products & Memberships shell === */}
            <Route path="/instructor/products" element={<ProductsShell />}>
              <Route index element={<Navigate to="list" replace />} />
              <Route path="list" element={<InstructorProducts />} />
              <Route
                path="memberships"
                element={
                  <ModuleLockGuard featureKey={FEATURE_KEYS.VIP_MEMBERSHIP}>
                    <ProductsMembershipPage />
                  </ModuleLockGuard>
                }
              />
            </Route>
            <Route path="/instructor/products/new" element={<InstructorProductForm />} />
            <Route path="/instructor/products/:productId" element={<InstructorProductForm />} />
            <Route path="/instructor/products/:productId/page" element={<ProductPageEditor />} />
            <Route path="/instructor/products/:productId/reviews" element={<InstructorEntityReviews />} />
            <Route
              path="/instructor/products/memberships/landing"
              element={
                <ModuleLockGuard featureKey={FEATURE_KEYS.VIP_MEMBERSHIP}>
                  <ProductsMembershipLandingEditor />
                </ModuleLockGuard>
              }
            />
            {/* Legacy product redirects */}
            <Route path="/instructor/teaching/products/:productId" element={<LegacyRedirect to="/instructor/products/:productId" />} />
            <Route path="/instructor/teaching/products/:productId/page" element={<LegacyRedirect to="/instructor/products/:productId/page" />} />
            <Route path="/instructor/teaching/products/:productId/reviews" element={<LegacyRedirect to="/instructor/products/:productId/reviews" />} />

            {/* === Teaching shell (courses + sessions + question-bank) === */}
            <Route path="/instructor/teaching" element={<TeachingShell />}>
              <Route index element={<Navigate to="courses" replace />} />
              <Route path="courses" element={<InstructorCourses />} />
              <Route path="courses/new" element={<InstructorCourseForm />} />
              <Route path="courses/:courseId" element={<InstructorCourseForm />} />
              <Route path="courses/:courseId/content" element={<InstructorCourseContent />} />
              <Route path="courses/:courseId/page" element={<InstructorCoursePageEditor />} />
              <Route path="courses/:courseId/reviews" element={<InstructorEntityReviews />} />
              <Route path="courses/:courseId/lessons/:lessonId" element={<InstructorLessonEditor />} />
              <Route
                path="courses/:courseId/quizzes/:quizId"
                element={
                  <ModuleLockGuard featureKey={FEATURE_KEYS.QUIZZES}>
                    <InstructorQuizEditor />
                  </ModuleLockGuard>
                }
              />
              <Route
                path="sessions"
                element={
                  <ModuleLockGuard featureKey={FEATURE_KEYS.LIVE_SESSIONS}>
                    <InstructorSessions />
                  </ModuleLockGuard>
                }
              />
              <Route
                path="sessions/:sessionId"
                element={
                  <ModuleLockGuard featureKey={FEATURE_KEYS.LIVE_SESSIONS}>
                    <InstructorSessionDetail />
                  </ModuleLockGuard>
                }
              />
              <Route path="sessions/:sessionId/reviews" element={<InstructorEntityReviews />} />
              <Route
                path="sessions/:sessionId/page"
                element={
                  <ModuleLockGuard featureKey={FEATURE_KEYS.LIVE_SESSIONS}>
                    <InstructorSessionPageEditor />
                  </ModuleLockGuard>
                }
              />
              <Route
                path="session-types/:sessionTypeId/page"
                element={
                  <ModuleLockGuard featureKey={FEATURE_KEYS.LIVE_SESSIONS}>
                    <InstructorSessionTypePageEditor />
                  </ModuleLockGuard>
                }
              />
              <Route
                path="question-bank"
                element={
                  <ModuleLockGuard featureKey={FEATURE_KEYS.QUESTION_BANK}>
                    <InstructorQuestionBank />
                  </ModuleLockGuard>
                }
              />
            </Route>

            {/* Legacy teaching redirects */}
            <Route path="/instructor/courses" element={<LegacyRedirect to="/instructor/teaching/courses" />} />
            <Route path="/instructor/courses/new" element={<LegacyRedirect to="/instructor/teaching/courses/new" />} />
            <Route path="/instructor/courses/:courseId" element={<LegacyRedirect to="/instructor/teaching/courses/:courseId" />} />
            <Route path="/instructor/courses/:courseId/content" element={<LegacyRedirect to="/instructor/teaching/courses/:courseId/content" />} />
            <Route path="/instructor/courses/:courseId/page" element={<LegacyRedirect to="/instructor/teaching/courses/:courseId/page" />} />
            <Route path="/instructor/courses/:courseId/lessons/:lessonId" element={<LegacyRedirect to="/instructor/teaching/courses/:courseId/lessons/:lessonId" />} />
            <Route path="/instructor/courses/:courseId/quizzes/:quizId" element={<LegacyRedirect to="/instructor/teaching/courses/:courseId/quizzes/:quizId" />} />
            <Route path="/instructor/sessions" element={<LegacyRedirect to="/instructor/teaching/sessions" />} />
            <Route path="/instructor/sessions/:sessionId" element={<LegacyRedirect to="/instructor/teaching/sessions/:sessionId" />} />
            <Route path="/instructor/question-bank" element={<LegacyRedirect to="/instructor/teaching/question-bank" />} />

            {/* === People shell (students + team) === */}
            <Route path="/instructor/people" element={<PeopleShell />}>
              <Route index element={<Navigate to="students" replace />} />
              <Route path="students" element={<InstructorStudents />} />
              <Route path="students/:studentId" element={<InstructorStudentDetail />} />
              <Route path="team" element={<InstructorTeam />} />
            </Route>
            {/* Legacy people redirects */}
            <Route path="/instructor/students" element={<LegacyRedirect to="/instructor/people/students" />} />
            <Route path="/instructor/students/:studentId" element={<LegacyRedirect to="/instructor/people/students/:studentId" />} />
            <Route path="/instructor/team" element={<LegacyRedirect to="/instructor/people/team" />} />

            {/* === Communications shell === */}
            <Route path="/instructor/communications" element={<CommunicationsShell />}>
              <Route index element={<Navigate to="messages" replace />} />
              <Route path="messages" element={<InstructorMessages />} />
              <Route path="comments" element={<InstructorComments />} />
              <Route path="emails" element={<EmailsSubShell />}>
                <Route index element={<Navigate to="templates" replace />} />
                <Route path="templates" element={<InstructorEmailTemplates />} />
                <Route path="templates/:triggerType" element={<InstructorEmailTemplateEditor />} />
                <Route path="logs" element={<InstructorEmailLogs />} />
                <Route path="campaign" element={<InstructorEmailCampaign />} />
              </Route>
              <Route path="notifications" element={<InstructorNotificationHistory />} />
              <Route path="support" element={<InstructorSupport />} />
              <Route path="feature-requests" element={<InstructorFeatureRequests />} />
              <Route path="reviews" element={<InstructorReviewsLayout />}>
                <Route index element={<Navigate to="list" replace />} />
                <Route path="list" element={<InstructorReviewsList />} />
                <Route path="analytics" element={<InstructorReviewsAnalytics />} />
                <Route path="settings" element={<InstructorReviewsSettings />} />
              </Route>
              <Route path="platform-feedback" element={<Navigate to="/instructor/dashboard" replace />} />
            </Route>
            {/* Legacy communications redirects */}
            <Route path="/instructor/messages" element={<LegacyRedirect to="/instructor/communications/messages" />} />
            <Route path="/instructor/comments" element={<LegacyRedirect to="/instructor/communications/comments" />} />
            <Route path="/instructor/email-templates" element={<LegacyRedirect to="/instructor/communications/emails/templates" />} />
            <Route path="/instructor/email-templates/:triggerType" element={<LegacyRedirect to="/instructor/communications/emails/templates/:triggerType" />} />
            <Route path="/instructor/email-logs" element={<LegacyRedirect to="/instructor/communications/emails/logs" />} />
            <Route path="/instructor/email-campaign" element={<LegacyRedirect to="/instructor/communications/emails/campaign" />} />
            <Route path="/instructor/notifications" element={<LegacyRedirect to="/instructor/communications/notifications" />} />
            <Route path="/instructor/feature-requests" element={<LegacyRedirect to="/instructor/communications/feature-requests" />} />

            {/* === Reviews hub (standalone) === */}
            <Route path="/instructor/reviews" element={<ReviewsHubLayout />}>
              <Route index element={<Navigate to="overview" replace />} />
              <Route path="overview" element={<ReviewsHubOverview />} />
              <Route path="academy" element={<ReviewsHubAcademy />} />
              <Route path="content" element={<ReviewsHubContent />} />
              <Route path="students" element={<InstructorReviewsLayout />}>
                <Route index element={<Navigate to="list" replace />} />
                <Route path="list" element={<InstructorReviewsList />} />
                <Route path="analytics" element={<InstructorReviewsAnalytics />} />
                <Route path="settings" element={<InstructorReviewsSettings />} />
              </Route>
              <Route path="platform" element={<Navigate to="/instructor/reviews/overview" replace />} />
            </Route>

            {/* === Marketing shell (invites + grants + coupons) === */}
            <Route path="/instructor/marketing" element={<MarketingShell />}>
              <Route index element={<MarketingDefaultRedirect />} />
              <Route
                path="invites"
                element={
                  <ModuleLockGuard featureKey={FEATURE_KEYS.GRANTS_AND_INVITES}>
                    <InstructorSentInvites />
                  </ModuleLockGuard>
                }
              />
              <Route
                path="grants"
                element={
                  <ModuleLockGuard featureKey={FEATURE_KEYS.GRANTS_AND_INVITES}>
                    <InstructorMarketingGrants />
                  </ModuleLockGuard>
                }
              />
              <Route path="coupons" element={<InstructorCoupons />} />
              <Route
                path="announcements"
                element={
                  <ModuleLockGuard featureKey={FEATURE_KEYS.BANNERS}>
                    <InstructorAnnouncements />
                  </ModuleLockGuard>
                }
              />
              {/* Legacy: redirect old separate banner routes to merged tab */}
              <Route path="announcements-public" element={<Navigate to="../announcements" replace />} />
              <Route path="announcements-students" element={<Navigate to="../announcements" replace />} />
            </Route>
            {/* Legacy marketing redirects */}
            <Route path="/instructor/sent-invites" element={<LegacyRedirect to="/instructor/marketing/invites" />} />
            <Route path="/instructor/coupons" element={<LegacyRedirect to="/instructor/marketing/coupons" />} />

            {/* === Wallet shell (overview + transfers) === */}
            <Route path="/instructor/wallet" element={<WalletShell />}>
              <Route index element={<Navigate to="overview" replace />} />
              <Route path="overview" element={<InstructorWalletOverview />} />
              <Route path="transfers" element={<InstructorBankTransferReview />} />
              <Route
                path="membership-transfers"
                element={
                  <ModuleLockGuard featureKey={FEATURE_KEYS.VIP_MEMBERSHIP}>
                    <InstructorMembershipBankReview />
                  </ModuleLockGuard>
                }
              />
            </Route>
            {/* Legacy wallet redirects */}
            <Route path="/instructor/bank-transfer-review" element={<LegacyRedirect to="/instructor/wallet/transfers" />} />
            <Route path="/instructor/wallet/bank-transfers" element={<LegacyRedirect to="/instructor/wallet/transfers" />} />
            {/* Legacy routes from older edge-function notifications & emails — redirect to canonical wallet routes with ?focus=:id */}
            <Route path="/instructor/payments/bank-transfers/:id" element={<BankTransferFocusRedirect base="/instructor/wallet/transfers" />} />
            <Route path="/instructor/payments/membership-bank/:id" element={<BankTransferFocusRedirect base="/instructor/wallet/membership-transfers" />} />
            <Route path="/instructor/bank-transfers" element={<LegacyRedirect to="/instructor/wallet/transfers" />} />

            {/* === Certificates (unchanged) === */}
            <Route
              path="/instructor/certificates"
              element={
                <ModuleLockGuard featureKey={FEATURE_KEYS.CERTIFICATES_AUTO}>
                  <InstructorCertificateTemplates />
                </ModuleLockGuard>
              }
            />
            <Route path="/instructor/certificates/issue" element={<Navigate to="/instructor/certificates" replace />} />

            {/* === Learning Center (instructor consumption) === */}
            <Route path="/instructor/learning" element={<InstructorLearning />} />
            <Route path="/instructor/learning/c/:slug" element={<InstructorLearningCategory />} />
            <Route path="/instructor/learning/a/:slug" element={<InstructorLearningArticle />} />

            {/* === Settings shell (nested) === */}
            <Route path="/instructor/settings" element={<SettingsShell />}>
              <Route index element={<Navigate to="branding" replace />} />
              {/* Merged primary tabs */}
              <Route path="branding" element={<SettingsIdentityPage />} />
              <Route path="domains" element={<SettingsSitePresencePage />} />
              <Route path="currency" element={<SettingsFinancePage />} />
              <Route path="billing" element={<SettingsBillingPlanPage />} />
              <Route path="notifications" element={<SettingsNotificationsPage />} />
              <Route path="integrations" element={<SettingsIntegrationsPage />} />
              <Route path="tracking" element={<SettingsTrackingPage />} />
              <Route path="export" element={<InstructorDataExport />} />
              {/* Legacy sub-paths → redirect to merged tabs with ?section= */}
              <Route path="general" element={<Navigate to="/instructor/settings/branding?section=general" replace />} />
              <Route path="site" element={<Navigate to="/instructor/settings/domains?section=site" replace />} />
              <Route path="legal" element={<Navigate to="/instructor/settings/domains?section=legal" replace />} />
              <Route path="payment-methods" element={<Navigate to="/instructor/settings/currency?section=payment-methods" replace />} />
              <Route path="receipt-template" element={<Navigate to="/instructor/settings/currency?section=receipt" replace />} />
              <Route path="subscription" element={<Navigate to="/instructor/settings/billing?section=subscription" replace />} />
              <Route path="membership" element={<LegacyRedirect to="/instructor/products/memberships" />} />
            </Route>
            {/* Legacy settings redirects */}
            <Route path="/instructor/domains" element={<LegacyRedirect to="/instructor/settings/domains?section=domain" />} />
            <Route path="/instructor/export" element={<LegacyRedirect to="/instructor/settings/export" />} />
          </Route>

          <Route path="/admin/login" element={<AdminLogin />} />
          <Route path="/login/admin" element={<AdminLogin />} />
          <Route path="/admin" element={<AdminLayout />}>
            <Route path="dashboard" element={<AdminDashboard />} />
            <Route path="academies" element={<AdminAcademies />} />
            <Route path="academies/:id" element={<AcademyDetail />} />
            <Route path="domains" element={<AdminDomains />} />
            <Route path="academies/:id/reviews" element={<AdminAcademyReviews />} />
            <Route path="reviews" element={<AdminReviewsLayout />}>
              <Route index element={<Navigate to="overview" replace />} />
              <Route path="overview" element={<AdminReviewsOverview />} />
              <Route path="academies" element={<AdminReviewsAcademies />} />
              <Route path="content" element={<AdminReviewsContent />} />
              <Route path="platform" element={<AdminReviewsPlatform />} />
            </Route>
            <Route path="instructors" element={<AdminInstructors />} />
            <Route path="subscriptions" element={<AdminSubscriptions />} />
            <Route path="payments" element={<AdminPayments />} />
            <Route path="students" element={<AdminStudents />} />
            <Route path="students/:id" element={<StudentDetail />} />
            <Route path="inbox" element={<AdminInboxShell />}>
              <Route index element={<Navigate to="notifications" replace />} />
              <Route path="notifications" element={<AdminNotifications />} />
              <Route path="escalations" element={<AdminBankTransferEscalations />} />
              <Route path="reports" element={<AdminSupportReports />} />
            </Route>
            <Route path="notifications" element={<Navigate to="/admin/inbox/notifications" replace />} />
            <Route path="bank-transfer-escalations" element={<Navigate to="/admin/inbox/escalations" replace />} />
            <Route path="audit-log" element={<AdminAuditLog />} />
            <Route path="team" element={<AdminTeam />} />
            <Route path="operator-health" element={<AdminOperatorHealth />} />
            <Route path="auth-email-center" element={<AdminAuthEmailCenter />} />
            <Route path="settings" element={<PlatformSettings />} />
            <Route path="plan-matrix" element={<PlanMatrix />} />
            <Route path="export" element={<AdminDataExport />} />
            <Route path="production-releases" element={<AdminProductionReleases />} />
            <Route path="allam" element={<AdminAllamDashboard />} />
            <Route path="dashboard/support" element={<AdminSupportAnalyticsHub />} />
            <Route path="marketing" element={<AdminMarketingShell />}>
              <Route index element={<Navigate to="articles" replace />} />
              <Route path="landing-seo" element={<AdminLandingSeo />} />
              <Route path="articles" element={<AdminArticles />} />
              <Route path="articles/:id" element={<AdminArticleEditor />} />
              <Route path="tags" element={<AdminArticleTags />} />
              <Route path="coupons" element={<PlatformCoupons />} />
              <Route path="redemptions" element={<CouponRedemptions />} />
              <Route path="learning-categories" element={<AdminLearningCategories />} />
              <Route path="learning-articles" element={<AdminLearningArticles />} />
              <Route path="learning-articles/:id" element={<AdminLearningArticleEditor />} />
              <Route path="learning-tags" element={<AdminLearningTags />} />
              <Route path="learning-settings" element={<AdminLearningSettings />} />
              <Route path="announcements-public" element={<AdminAnnouncementsPlatformPublic />} />
              <Route path="announcements-instructor" element={<AdminAnnouncementsInstructor />} />
            </Route>
          </Route>

          <Route path="/editor/canvas" element={<CanvasFrame />} />
          <Route path="/meet/:roomId" element={<MeetRedirect />} />
          <Route path="/email-unsubscribe" element={<EmailUnsubscribe />} />
          <Route path="/unsubscribe" element={<Unsubscribe />} />
          <Route path="/certificate/view/:certificateId" element={<CertificateView />} />
          <Route path="/c/:shareToken" element={<PublicCertificateView />} />
          <Route path="/receipt/view/:requestId" element={<ReceiptView />} />

          {/* Backward compatibility: /academy/:slug/* → /:slug/* */}
          <Route path="/academy/:slug/*" element={<AcademyRedirect />} />
          <Route path="/academy/:slug" element={<AcademyRedirect />} />

          <Route path="/:slug" element={<AcademySlugShell />}>
            {/* Public segments */}
            <Route index element={<AcademyHomeFallback />} />
            <Route path="login" element={<AcademyLogin />} />
            <Route path="signup" element={<AcademySignup />} />
            <Route path="google-handoff" element={<AcademyGoogleHandoff />} />
            <Route path="complete-profile" element={<CompleteProfile />} />

            {/* Unified academy public routes (single source of truth) */}
            {academyPublicRoutes()}

            {/* Unified student internal routes (single source of truth) */}
            {studentInternalRoutes()}

            {/* Unified legacy redirects (parity with custom domain) */}
            {studentLegacyRedirectRoutes()}

            {/* Unified academy 404 (parity with custom domain).
                MUST be the last child route so all known paths match first. */}
            {academyNotFoundRoute()}
          </Route>

          <Route path="*" element={<PageNotFound />} />
        </Routes>
      </Suspense>
    </AppErrorBoundary>
  );
};

function App() {
  return (
    <I18nProvider>
      <PlatformCurrencyProvider>
        <AuthProvider>
          <QueryClientProvider client={queryClientInstance}>
            <Router>
              <AuthenticatedApp />
            </Router>
            <Toaster />
            <SonnerToaster position="top-center" richColors closeButton dir="rtl" />
          </QueryClientProvider>
        </AuthProvider>
      </PlatformCurrencyProvider>
    </I18nProvider>
  );
}

export default App;
